/**
 * PerformanceTable
 *
 * 13-column grouped table showing rolling performance metrics per timekeeper.
 * Columns are grouped into Week, Month, and Year sections with colored header
 * bands, spacer gaps, and inset box-shadow outlines for visual grouping.
 * Positive deltas show a green dot, negative show red with parenthesized values.
 * Sub-headers are clickable to sort rows by any metric.
 */

import { type CSSProperties, useMemo, useState } from "react";
import { ArrowUp, ArrowDown } from "lucide-react";
import { PerformanceRow } from "../../types/dashboard";
import { PERF_TABLE_COLORS } from "../../config/dashboardTheme";

/* ------------------------------------------------------------------ */
/*  Sort types                                                        */
/* ------------------------------------------------------------------ */

type SortKey =
  | "name"
  | "utilization_pct"
  | "curr_week" | "avg_week" | "delta_week" | "pct_week"
  | "curr_month" | "avg_month" | "delta_month" | "pct_month"
  | "curr_ytd" | "prior_ytd";

type SortDir = "asc" | "desc";

interface SortState {
  key: SortKey;
  dir: SortDir;
}

/* ------------------------------------------------------------------ */
/*  Props                                                             */
/* ------------------------------------------------------------------ */

interface PerformanceTableProps {
  rows: PerformanceRow[];
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

/** Builds an inset box-shadow to outline column group boundaries. */
function groupShadow(color: string, ...sides: ("left" | "right" | "bottom")[]): CSSProperties {
  const parts = sides.map((s) =>
    s === "left"
      ? `inset 2px 0 0 0 ${color}`
      : s === "right"
        ? `inset -2px 0 0 0 ${color}`
        : `inset 0 -2px 0 0 ${color}`
  );
  return { boxShadow: parts.join(", ") };
}

function formatHours(value: number): string {
  return value.toFixed(1);
}

function formatDelta(current: number, average: number): string {
  return formatHours(current - average);
}

function formatPctChange(current: number, average: number): string {
  if (average === 0) return current > 0 ? "+100%" : "0%";
  const pct = ((current - average) / average) * 100;
  const sign = pct >= 0 ? "+" : "";
  return `${sign}${pct.toFixed(0)}%`;
}

/** Extract a comparable sort value for a given row + key. */
function getSortValue(row: PerformanceRow, key: SortKey): number | string {
  switch (key) {
    case "name":             return row.prof_name.toLowerCase();
    case "utilization_pct": return row.utilization_pct;
    case "curr_week":   return row.curr_week;
    case "avg_week":    return row.avg_week;
    case "delta_week":  return row.curr_week - row.avg_week;
    case "pct_week":    return row.avg_week === 0 ? (row.curr_week > 0 ? Infinity : 0) : (row.curr_week - row.avg_week) / row.avg_week;
    case "curr_month":  return row.curr_month;
    case "avg_month":   return row.avg_month;
    case "delta_month": return row.curr_month - row.avg_month;
    case "pct_month":   return row.avg_month === 0 ? (row.curr_month > 0 ? Infinity : 0) : (row.curr_month - row.avg_month) / row.avg_month;
    case "curr_ytd":    return row.curr_ytd;
    case "prior_ytd":   return row.prior_ytd;
  }
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                    */
/* ------------------------------------------------------------------ */

/** Clickable sub-header cell with sort arrow indicator. */
function SortableTh({
  sortKey,
  label,
  sublabel,
  activeSort,
  onSort,
  className,
  style,
}: {
  sortKey: SortKey;
  label: string;
  sublabel?: string;
  activeSort: SortState | null;
  onSort: (key: SortKey) => void;
  className: string;
  style?: CSSProperties;
}) {
  const isActive = activeSort?.key === sortKey;
  const Arrow = isActive
    ? activeSort.dir === "asc" ? ArrowUp : ArrowDown
    : null;

  return (
    <th
      className={`${className} cursor-pointer select-none transition-colors hover:text-foreground`}
      style={style}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex flex-col items-end leading-tight">
        <span className="inline-flex items-center gap-0.5">
          {label}
          {Arrow && <Arrow className="h-3 w-3 shrink-0" />}
        </span>
        {sublabel && <span className="text-[10px] font-normal whitespace-nowrap">{sublabel}</span>}
      </span>
    </th>
  );
}

function DeltaCell({
  current,
  average,
  style,
}: {
  current: number;
  average: number;
  style?: CSSProperties;
}) {
  const delta = current - average;
  const isPositive = delta >= 0;
  const color = isPositive
    ? PERF_TABLE_COLORS.positive
    : PERF_TABLE_COLORS.negative;

  return (
    <td className="px-3 py-3 text-right text-sm whitespace-nowrap" style={style}>
      <span className="inline-flex items-center gap-1.5">
        <span
          className="w-2 h-2 rounded-full shrink-0"
          style={{ backgroundColor: color }}
        />
        <span
          className="inline-block tabular-nums text-right min-w-[3.5rem]"
          style={{ color }}
        >
          {formatDelta(current, average)}
        </span>
      </span>
    </td>
  );
}

function PctCell({
  current,
  average,
  style,
}: {
  current: number;
  average: number;
  style?: CSSProperties;
}) {
  const delta = current - average;
  const isPositive = delta >= 0;
  const color = isPositive
    ? PERF_TABLE_COLORS.positive
    : PERF_TABLE_COLORS.negative;

  return (
    <td className="px-3 py-3 text-right text-sm whitespace-nowrap" style={style}>
      <span style={{ color }}>{formatPctChange(current, average)}</span>
    </td>
  );
}

/** Decorative spacer cell between column groups. */
function SpacerTd() {
  return <td className="w-2 p-0" aria-hidden="true" />;
}

/* ------------------------------------------------------------------ */
/*  Main component                                                    */
/* ------------------------------------------------------------------ */

export default function PerformanceTable({
  rows,
}: PerformanceTableProps) {
  const [sort, setSort] = useState<SortState | null>({ key: "utilization_pct", dir: "asc" });

  function handleSort(key: SortKey) {
    setSort((prev) => {
      if (prev?.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      // Name defaults to A→Z, numeric columns default to highest-first
      return { key, dir: key === "name" ? "asc" : "desc" };
    });
  }

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const { key, dir } = sort;
    return [...rows].sort((a, b) => {
      const aVal = getSortValue(a, key);
      const bVal = getSortValue(b, key);
      const cmp =
        typeof aVal === "string"
          ? (aVal as string).localeCompare(bVal as string)
          : (aVal as number) - (bVal as number);
      return dir === "asc" ? cmp : -cmp;
    });
  }, [rows, sort]);

  // Compute totals row (from unsorted source to avoid sort-dependency)
  const totals = rows.reduce(
    (acc, row) => ({
      hg_week: acc.hg_week + (row.hg_week ?? 0),
      curr_week: acc.curr_week + row.curr_week,
      avg_week: acc.avg_week + row.avg_week,
      curr_month: acc.curr_month + row.curr_month,
      avg_month: acc.avg_month + row.avg_month,
      curr_ytd: acc.curr_ytd + row.curr_ytd,
      prior_ytd: acc.prior_ytd + row.prior_ytd,
    }),
    {
      hg_week: 0,
      curr_week: 0,
      avg_week: 0,
      curr_month: 0,
      avg_month: 0,
      curr_ytd: 0,
      prior_ytd: 0,
    }
  );
  const totalUtilizationPct =
    totals.curr_week === 0
      ? 0
      : Math.min(100, (totals.hg_week / totals.curr_week) * 100);

  // Shorthand for group colors
  const ut = PERF_TABLE_COLORS.utilizationHeader;
  const wk = PERF_TABLE_COLORS.weekHeader;
  const mo = PERF_TABLE_COLORS.monthHeader;
  const yr = PERF_TABLE_COLORS.yearHeader;

  return (
    <div className="pb-8">
      {/* Scrollable table */}
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm border-collapse">
          {/* Tier 1: Group headers */}
          <thead>
            <tr>
              <th
                rowSpan={2}
                className="px-3 py-2 text-left font-semibold text-foreground bg-muted border-b border-border sticky left-0 z-10 cursor-pointer select-none transition-colors hover:text-foreground/80 [box-shadow:1px_0_0_0_var(--border)]"
                onClick={() => handleSort("name")}
              >
                <span className="inline-flex items-center gap-0.5">
                  Name
                  {sort?.key === "name" && (
                    sort.dir === "asc"
                      ? <ArrowUp className="h-3 w-3 shrink-0" />
                      : <ArrowDown className="h-3 w-3 shrink-0" />
                  )}
                </span>
              </th>
              <th
                colSpan={1}
                className="px-4 py-1.5 text-center font-semibold text-white border-b border-border"
                style={{ backgroundColor: PERF_TABLE_COLORS.utilizationHeader }}
              >
                UTILIZATION
              </th>
              {/* Spacer between Utilization and Week */}
              <th rowSpan={2} className="w-2 p-0" aria-hidden="true" />
              <th
                colSpan={4}
                className="px-4 py-1.5 text-center font-semibold text-white border-b border-border"
                style={{ backgroundColor: PERF_TABLE_COLORS.weekHeader }}
              >
                WEEK
              </th>
              {/* Spacer between Week and Month */}
              <th rowSpan={2} className="w-2 p-0" aria-hidden="true" />
              <th
                colSpan={4}
                className="px-4 py-1.5 text-center font-semibold text-white border-b border-border"
                style={{ backgroundColor: PERF_TABLE_COLORS.monthHeader }}
              >
                MONTH
              </th>
              {/* Spacer between Month and Year */}
              <th rowSpan={2} className="w-2 p-0" aria-hidden="true" />
              <th
                colSpan={4}
                className="px-4 py-1.5 text-center font-semibold text-white border-b border-border"
                style={{ backgroundColor: PERF_TABLE_COLORS.yearHeader }}
              >
                YEAR
              </th>
            </tr>
            {/* Tier 2: Sortable sub-headers */}
            <tr className="bg-muted/30 border-b border-border">
              {/* Utilization sub-header */}
              <SortableTh sortKey="utilization_pct" label="This Week" activeSort={sort} onSort={handleSort}
                className="px-4 py-1.5 text-right text-xs font-medium text-muted-foreground whitespace-nowrap"
                style={groupShadow(ut, "left", "right")}
              />
              {/* Week sub-headers */}
              <SortableTh sortKey="curr_week" label="Current" activeSort={sort} onSort={handleSort}
                className="pl-4 pr-3 py-1.5 text-right text-xs font-medium text-muted-foreground"
                style={groupShadow(wk, "left")}
              />
              <SortableTh sortKey="avg_week" label="Average" sublabel="(Prior 12 months)" activeSort={sort} onSort={handleSort}
                className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground"
              />
              <SortableTh sortKey="delta_week" label="Delta" activeSort={sort} onSort={handleSort}
                className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground"
              />
              <SortableTh sortKey="pct_week" label="% Chg" activeSort={sort} onSort={handleSort}
                className="pl-3 pr-4 py-1.5 text-right text-xs font-medium text-muted-foreground"
                style={groupShadow(wk, "right")}
              />
              {/* Month sub-headers */}
              <SortableTh sortKey="curr_month" label="Current" sublabel="(Rolling 4 weeks)" activeSort={sort} onSort={handleSort}
                className="pl-4 pr-3 py-1.5 text-right text-xs font-medium text-muted-foreground"
                style={groupShadow(mo, "left")}
              />
              <SortableTh sortKey="avg_month" label="Average" sublabel="(Prior 12 months)" activeSort={sort} onSort={handleSort}
                className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground"
              />
              <SortableTh sortKey="delta_month" label="Delta" activeSort={sort} onSort={handleSort}
                className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground"
              />
              <SortableTh sortKey="pct_month" label="% Chg" activeSort={sort} onSort={handleSort}
                className="pl-3 pr-4 py-1.5 text-right text-xs font-medium text-muted-foreground"
                style={groupShadow(mo, "right")}
              />
              {/* Year sub-headers */}
              <SortableTh sortKey="curr_ytd" label="Current YTD" activeSort={sort} onSort={handleSort}
                className="pl-4 pr-3 py-1.5 text-right text-xs font-medium text-muted-foreground whitespace-nowrap"
                style={groupShadow(yr, "left")}
              />
              <SortableTh sortKey="prior_ytd" label="Prior YTD" activeSort={sort} onSort={handleSort}
                className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground whitespace-nowrap"
              />
              <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">Delta</th>
              <th className="pl-3 pr-4 py-1.5 text-right text-xs font-medium text-muted-foreground" style={groupShadow(yr, "right")}>% Chg</th>
            </tr>
          </thead>

          <tbody>
            {sortedRows.map((row, i) => (
              <tr
                key={row.prof_id}
                className={`border-b border-border/50 hover:bg-muted/30 transition-colors ${
                  i % 2 === 0 ? "" : "bg-muted/10"
                }`}
              >
                {/* Name */}
                <td className="px-3 py-3 font-medium text-foreground whitespace-nowrap sticky left-0 bg-card z-10 [box-shadow:1px_0_0_0_var(--border)]">
                  {row.prof_name}
                </td>
                {/* Utilization */}
                <td className="px-4 py-3 text-right tabular-nums" style={groupShadow(ut, "left", "right")}>
                  {`${(row.utilization_pct ?? 0).toFixed(1)}%`}
                </td>
                <SpacerTd />
                {/* Week */}
                <td className="pl-4 pr-3 py-3 text-right tabular-nums" style={groupShadow(wk, "left")}>
                  {formatHours(row.curr_week)}
                </td>
                <td className="px-3 py-3 text-right tabular-nums text-muted-foreground">
                  {formatHours(row.avg_week)}
                </td>
                <DeltaCell current={row.curr_week} average={row.avg_week} />
                <PctCell current={row.curr_week} average={row.avg_week} style={{ ...groupShadow(wk, "right"), paddingRight: "1rem" }} />
                <SpacerTd />
                {/* Month */}
                <td className="pl-4 pr-3 py-3 text-right tabular-nums" style={groupShadow(mo, "left")}>
                  {formatHours(row.curr_month)}
                </td>
                <td className="px-3 py-3 text-right tabular-nums text-muted-foreground">
                  {formatHours(row.avg_month)}
                </td>
                <DeltaCell current={row.curr_month} average={row.avg_month} />
                <PctCell current={row.curr_month} average={row.avg_month} style={{ ...groupShadow(mo, "right"), paddingRight: "1rem" }} />
                <SpacerTd />
                {/* Year */}
                <td className="pl-4 pr-3 py-3 text-right tabular-nums" style={groupShadow(yr, "left")}>
                  {formatHours(row.curr_ytd)}
                </td>
                <td className="px-3 py-3 text-right tabular-nums text-muted-foreground">
                  {formatHours(row.prior_ytd)}
                </td>
                {row.curr_ytd === 0 || row.prior_ytd === 0
                  ? <td className="px-3 py-3 text-right text-sm text-muted-foreground">N/A</td>
                  : <DeltaCell current={row.curr_ytd} average={row.prior_ytd} />}
                {row.curr_ytd === 0 || row.prior_ytd === 0
                  ? <td className="pl-3 pr-4 py-3 text-right text-sm text-muted-foreground" style={groupShadow(yr, "right")}>N/A</td>
                  : <PctCell current={row.curr_ytd} average={row.prior_ytd} style={{ ...groupShadow(yr, "right"), paddingRight: "1rem" }} />}
              </tr>
            ))}

            {/* Totals row */}
            {rows.length > 0 && (
              <tr className="border-t-2 border-border bg-muted/50 font-semibold">
                <td className="px-3 py-3 text-foreground sticky left-0 bg-muted z-10 [box-shadow:1px_0_0_0_var(--border)]">
                  Total
                </td>
                {/* Utilization total */}
                <td className="px-4 py-3 text-right tabular-nums" style={groupShadow(ut, "left", "right", "bottom")}>
                  {`${totalUtilizationPct.toFixed(1)}%`}
                </td>
                <SpacerTd />
                <td className="pl-4 pr-3 py-3 text-right tabular-nums" style={groupShadow(wk, "left", "bottom")}>
                  {formatHours(totals.curr_week)}
                </td>
                <td className="px-3 py-3 text-right tabular-nums text-muted-foreground" style={groupShadow(wk, "bottom")}>
                  {formatHours(totals.avg_week)}
                </td>
                <DeltaCell
                  current={totals.curr_week}
                  average={totals.avg_week}
                  style={groupShadow(wk, "bottom")}
                />
                <PctCell
                  current={totals.curr_week}
                  average={totals.avg_week}
                  style={{ ...groupShadow(wk, "right", "bottom"), paddingRight: "1rem" }}
                />
                <SpacerTd />
                <td className="pl-4 pr-3 py-3 text-right tabular-nums" style={groupShadow(mo, "left", "bottom")}>
                  {formatHours(totals.curr_month)}
                </td>
                <td className="px-3 py-3 text-right tabular-nums text-muted-foreground" style={groupShadow(mo, "bottom")}>
                  {formatHours(totals.avg_month)}
                </td>
                <DeltaCell
                  current={totals.curr_month}
                  average={totals.avg_month}
                  style={groupShadow(mo, "bottom")}
                />
                <PctCell
                  current={totals.curr_month}
                  average={totals.avg_month}
                  style={{ ...groupShadow(mo, "right", "bottom"), paddingRight: "1rem" }}
                />
                <SpacerTd />
                <td className="pl-4 pr-3 py-3 text-right tabular-nums" style={groupShadow(yr, "left", "bottom")}>
                  {formatHours(totals.curr_ytd)}
                </td>
                <td className="px-3 py-3 text-right tabular-nums text-muted-foreground" style={groupShadow(yr, "bottom")}>
                  {formatHours(totals.prior_ytd)}
                </td>
                {totals.curr_ytd === 0 || totals.prior_ytd === 0
                  ? <td className="px-3 py-3 text-right text-sm text-muted-foreground" style={groupShadow(yr, "bottom")}>N/A</td>
                  : <DeltaCell current={totals.curr_ytd} average={totals.prior_ytd} style={groupShadow(yr, "bottom")} />}
                {totals.curr_ytd === 0 || totals.prior_ytd === 0
                  ? <td className="pl-3 pr-4 py-3 text-right text-sm text-muted-foreground" style={groupShadow(yr, "right", "bottom")}>N/A</td>
                  : <PctCell current={totals.curr_ytd} average={totals.prior_ytd} style={{ ...groupShadow(yr, "right", "bottom"), paddingRight: "1rem" }} />}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {rows.length === 0 && (
        <p className="text-center text-muted-foreground text-sm py-8">
          No billing data available for the selected week.
        </p>
      )}
    </div>
  );
}
